import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Globe, Search, Check, AlertTriangle, ChevronRight, Edit3, Save } from 'lucide-react';

const TranslationsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [selectedLang, setSelectedLang] = useState(null);
  const [langDetail, setLangDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filterMissing, setFilterMissing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/translations').then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [api]);

  const loadLangDetail = async (code) => {
    setSelectedLang(code);
    setLangDetail(null);
    const r = await api.get(`/admin/translations/${code}`);
    setLangDetail(r.data);
  };

  const handleSave = async (key) => {
    setSaving(true);
    try {
      await api.put(`/admin/translations/${selectedLang}`, { key, value: editValue });
      setLangDetail(prev => ({
        ...prev,
        entries: prev.entries.map(e => e.key === key ? { ...e, value: editValue, missing: false } : e),
        translated: prev.translated + (prev.entries.find(e => e.key === key)?.missing ? 1 : 0)
      }));
      setEditing(null);
    } catch { /* silent */ }
    setSaving(false);
  };

  const filtered = langDetail?.entries?.filter(e => {
    if (filterMissing && !e.missing) return false;
    if (search && !e.key.toLowerCase().includes(search.toLowerCase()) && !e.fr_value.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }) || [];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20 text-gray-400" data-testid="translations-page">
          Chargement...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="translations-page">
        <PageHeader
          title="Gestion des traductions"
          subtitle={`Plateforme i18n — ${stats?.reference_count || 0} clés de référence`}
          backTo="/dashboard"
          icon={Globe}
          testId="translations-page-header"
        />

      {/* Language overview */}
      {!selectedLang && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stats.languages.map(l => (
            <button key={l.code} onClick={() => loadLangDetail(l.code)}
              className="card-dark p-4 text-left hover:border-purple-500/30 transition-colors group"
              data-testid={`lang-card-${l.code}`}>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl">{l.flag}</span>
                <div>
                  <div className="font-semibold text-white text-sm">{l.name}</div>
                  <div className="text-xs text-gray-500">{l.code}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-600 ml-auto group-hover:text-purple-400 transition-colors" />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${l.coverage}%`,
                    background: l.coverage === 100 ? '#22c55e' : l.coverage > 90 ? '#f59e0b' : '#ef4444'
                  }} />
                </div>
                <span className="text-xs font-mono" style={{
                  color: l.coverage === 100 ? '#22c55e' : l.coverage > 90 ? '#f59e0b' : '#ef4444'
                }}>{l.coverage}%</span>
              </div>
              {l.missing_count > 0 && (
                <div className="mt-2 text-xs text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {l.missing_count} clés manquantes
                </div>
              )}
              {l.missing_count === 0 && (
                <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Complet
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Language detail */}
      {selectedLang && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => { setSelectedLang(null); setLangDetail(null); setSearch(''); setFilterMissing(false); }}
              className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-300 text-sm hover:bg-white/10 transition-colors" data-testid="back-to-langs">
              Retour
            </button>
            {langDetail && (
              <div className="flex items-center gap-2">
                <span className="text-xl">{langDetail.flag}</span>
                <span className="font-bold text-white">{langDetail.name}</span>
                <span className="text-sm text-gray-400">({langDetail.translated}/{langDetail.total})</span>
              </div>
            )}
          </div>

          {langDetail && (
            <>
              <div className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher une clé..."
                    className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                    data-testid="translation-search" />
                </div>
                <button onClick={() => setFilterMissing(!filterMissing)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterMissing ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
                  data-testid="filter-missing-btn">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Manquantes ({langDetail.entries.filter(e => e.missing).length})
                </button>
              </div>

              <div className="card-dark overflow-hidden">
                <div className="grid grid-cols-[200px_1fr_1fr_60px] gap-2 px-4 py-3 border-b border-white/10 text-xs font-semibold text-gray-500 uppercase">
                  <div>Clé</div>
                  <div>Français (référence)</div>
                  <div>Traduction</div>
                  <div></div>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {filtered.slice(0, 100).map(entry => (
                    <div key={entry.key} className={`grid grid-cols-[200px_1fr_1fr_60px] gap-2 px-4 py-2.5 border-b border-white/5 items-center ${entry.missing ? 'bg-amber-500/5' : ''}`}>
                      <div className="text-xs font-mono text-gray-400 truncate" title={entry.key}>{entry.key}</div>
                      <div className="text-sm text-gray-300 truncate" title={entry.fr_value}>{entry.fr_value}</div>
                      {editing === entry.key ? (
                        <div className="flex gap-1">
                          <input value={editValue} onChange={e => setEditValue(e.target.value)}
                            className="flex-1 px-2 py-1 rounded bg-white/10 border border-purple-500/50 text-white text-sm focus:outline-none"
                            autoFocus onKeyDown={e => e.key === 'Enter' && handleSave(entry.key)}
                            data-testid={`edit-input-${entry.key}`} />
                          <button onClick={() => handleSave(entry.key)} disabled={saving}
                            className="p-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30" data-testid={`save-btn-${entry.key}`}>
                            <Save className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className={`text-sm truncate ${entry.missing ? 'text-amber-400 italic' : 'text-white'}`} title={entry.value || 'Non traduit'}>
                          {entry.value || '— non traduit —'}
                        </div>
                      )}
                      <div>
                        <button onClick={() => { setEditing(entry.key); setEditValue(entry.value || ''); }}
                          className="p-1 rounded text-gray-500 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                          data-testid={`edit-btn-${entry.key}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {filtered.length > 100 && (
                    <div className="px-4 py-3 text-center text-sm text-gray-500">
                      {filtered.length - 100} entrées supplémentaires... Utilisez la recherche pour filtrer.
                    </div>
                  )}
                  {filtered.length === 0 && (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">Aucun résultat</div>
                  )}
                </div>
              </div>
            </>
          )}

          {!langDetail && <div className="text-center text-gray-400 py-8">Chargement...</div>}
        </div>
      )}
      </div>
    </Layout>
  );
};

export default TranslationsPage;
